import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

export default function ArticlePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.boats';

  return (
    <>
      <Navbar />
      <main>
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Ultimate Kakobuy Spreadsheet Guide: How to Shop Smarter in 2026",
            "description": "Everything you need to know about using the Kakobuy Spreadsheet database — from category navigation and product discovery to safety tips and advanced search strategies.",
            "author": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet Hub"
            },
            "datePublished": "2026-07-09",
            "dateModified": "2026-07-09",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": siteUrl + "/kakobuy-spreadsheet-ultimate-guide-2026"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet Hub",
              "url": siteUrl
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Is the Kakobuy Spreadsheet free to use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, the spreadsheet database is freely accessible. You can browse categories, view product links, and access guides without any cost."
                }
              },
              {
                "@type": "Question",
                "name": "How often is the Kakobuy Spreadsheet updated?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The database receives frequent updates — typically on a weekly basis with new product entries, updated links, and refreshed category content."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need any special software to use the spreadsheet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, the Kakobuy Spreadsheet is designed to be accessible through a standard web browser. You can access it from any device without installing any additional software."
                }
              },
              {
                "@type": "Question",
                "name": "Is the spreadsheet safe and reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, the database is maintained with quality and safety in mind. We always recommend following the safety checklist: verify links, check QC photos, calculate total costs, and research sellers."
                }
              },
              {
                "@type": "Question",
                "name": "What makes Kakobuy Spreadsheet different from other product databases?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Kakobuy Spreadsheet offers the most comprehensive category coverage with 14 categories, frequent updates, beginner-friendly guides, and an integrated safety center."
                }
              }
            ]
          })}
        </script>
        {/* Article Hero */}
        <section className="relative w-full py-24 md:py-32 px-4 md:px-6 lg:px-10 bg-gradient-to-br from-background-100 via-background-50 to-accent-50/40">
          <div className="absolute top-16 left-10 w-56 h-56 rounded-full bg-primary-100/30 blur-3xl"></div>
          <div className="absolute bottom-10 right-16 w-72 h-72 rounded-full bg-accent-100/20 blur-3xl"></div>

          <div className="relative max-w-3xl mx-auto text-center">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-400">Kakobuy Spreadsheet Ultimate Guide</span>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-[1.12] mb-5">
              The Ultimate Kakobuy Spreadsheet Guide: How to Shop Smarter in 2026
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto mb-6 leading-relaxed">
              Everything you need to know about using the Kakobuy Spreadsheet database — from category navigation and product discovery to safety tips and advanced search strategies.
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span>Updated July 9, 2026</span>
              </div>
              <span className="text-foreground-300">·</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                <span>15 min read</span>
              </div>
              <span className="text-foreground-300">·</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                <span>By Kakobuy Hub Team</span>
              </div>
            </div>
          </div>

          {/* Article hero image */}
          <div className="relative max-w-4xl mx-auto mt-10">
            <div className="w-full h-52 md:h-64 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20a%20laptop%20displaying%20organized%20spreadsheet%20grid%20and%20product%20thumbnails%2C%20a%20coffee%20cup%20and%20notebook%20beside%20it%2C%20warm%20cream%20walls%20with%20natural%20window%20light%2C%20soft%20shadows%2C%20editorial%20photography%20with%20clean%20professional%20aesthetic%2C%20calm%20focused%20atmosphere&width=1200&height=500&seq=article-hero-main-v3-2026&orientation=landscape"
                alt="Kakobuy Spreadsheet Ultimate Guide 2026 - organized product database"
                title="Kakobuy Spreadsheet Ultimate Guide"
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full py-12 md:py-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-[720px] mx-auto">
            <div className="prose-custom text-foreground-800 leading-relaxed">

              {/* Intro */}
              <p className="text-base md:text-lg mb-8">
                If you have ever spent hours hunting for products online, you know how exhausting the process can be. Endless scrolling, broken links, unreliable sellers — it all adds up to frustration. That is exactly where the <strong>Kakobuy Spreadsheet</strong> comes in. This organized product discovery database is changing the way shoppers find, compare, and purchase items across multiple categories and platforms. In this comprehensive guide, we will walk you through everything you need to know about using the Kakobuy Spreadsheet effectively — whether you are a complete beginner or someone looking to level up your shopping workflow.
              </p>

              {/* Table of Contents */}
              <div className="bg-background-100 rounded-2xl p-6 mb-10 border border-background-200/60">
                <h2 className="font-heading font-bold text-foreground-950 text-lg mb-4">
                  Table of Contents
                </h2>
                <ol className="space-y-2 text-sm text-foreground-700 list-decimal list-inside">
                  <li><a href="#what-is" className="text-primary-500 hover:text-primary-600">What Is the Kakobuy Spreadsheet?</a></li>
                  <li><a href="#how-it-works" className="text-primary-500 hover:text-primary-600">How the Kakobuy Spreadsheet Works</a></li>
                  <li><a href="#categories" className="text-primary-500 hover:text-primary-600">Understanding the 14 Product Categories</a></li>
                  <li><a href="#benefits" className="text-primary-500 hover:text-primary-600">Key Benefits of Using a Product Spreadsheet</a></li>
                  <li><a href="#comparison" className="text-primary-500 hover:text-primary-600">Kakobuy vs Other Spreadsheet Platforms</a></li>
                  <li><a href="#safety" className="text-primary-500 hover:text-primary-600">Safety Guide: How to Shop Securely</a></li>
                  <li><a href="#tips" className="text-primary-500 hover:text-primary-600">Advanced Tips for Power Shoppers</a></li>
                  <li><a href="#faq" className="text-primary-500 hover:text-primary-600">Frequently Asked Questions</a></li>
                </ol>
              </div>

              {/* Section 1 */}
              <section id="what-is" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  1. What Is the Kakobuy Spreadsheet?
                </h2>
                <p className="mb-4">
                  The <strong>Kakobuy Spreadsheet</strong> is a structured product discovery database designed to help shoppers quickly locate curated items across a wide range of categories. Unlike traditional search methods where you manually browse through countless listings, the spreadsheet organizes everything into clean, searchable categories — making the entire process dramatically faster and more efficient.
                </p>
                <p className="mb-4">
                  At its core, the <strong>Kakobuy Spreadsheet</strong> functions as a centralized hub that aggregates product links, pricing information, quality check photos, and seller details. Whether you are looking for shoes, clothing, bags, accessories, or unique lifestyle items, the database provides a single point of access to thousands of organized finds.
                </p>
                <p className="mb-4">
                  The concept behind the spreadsheet is straightforward: instead of spending hours manually searching for products across different platforms, you can rely on a pre-organized resource that has already done the heavy lifting. The database is regularly updated with new entries, ensuring that you always have access to the latest product discoveries. This makes it especially valuable for shoppers who want to stay ahead of trends without dedicating excessive time to manual browsing.
                </p>
                <p>
                  The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Kakobuy Hub website</Link> serves as a gateway to this database, offering category navigation, comparison tools, and educational resources that help you make informed shopping decisions.
                </p>
              </section>

              {/* Section 2 */}
              <section id="how-it-works" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  2. How the Kakobuy Spreadsheet Works
                </h2>
                <p className="mb-4">
                  Using the Kakobuy Spreadsheet is refreshingly simple. The database is organized into logical categories that mirror how most people think about shopping. You do not need any technical skills or special software — just the ability to browse categories and click on the products that interest you.
                </p>
                <p className="mb-4">
                  Each entry in the spreadsheet includes essential information: the product name, a direct link to the item, pricing details, available sizes or variants, and often quality check (QC) photos so you can inspect the product before making a purchase decision. Some entries also include seller ratings and community feedback, giving you additional confidence in your choices.
                </p>
                <p className="mb-4">
                  The organization system is what sets the Kakobuy Spreadsheet apart from basic product lists. Categories are thoughtfully arranged, making it easy to browse related items. If you are looking at shoes, for example, you can quickly filter by style, brand, or price range. This structured approach eliminates the noise and clutter that plague traditional search results.
                </p>
                <p>
                  For new users, the learning curve is practically nonexistent. The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">homepage navigation</Link> provides clear paths to every category, and the dropdown selector lets you jump directly to the section you need. In less than a minute, you can go from knowing nothing about the spreadsheet to browsing through curated product collections.
                </p>
              </section>

              {/* Section 3 */}
              <section id="categories" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  3. Understanding the 14 Product Categories
                </h2>
                <p className="mb-4">
                  One of the standout features of the Kakobuy Spreadsheet is its comprehensive category system. With 14 distinct categories, the database covers virtually every type of product a shopper might be interested in. Let us take a closer look at each one:
                </p>
                <div className="space-y-4 mb-6">
                  {[
                    { name: 'Shoes', desc: 'From sneakers to formal footwear, the shoes category is one of the largest in the database, featuring hundreds of curated finds across popular brands and styles.' },
                    { name: 'Hoodies / Sweaters', desc: 'Cozy, stylish, and comfortable — this category covers everything from oversized hoodies to premium knit sweaters suitable for any season.' },
                    { name: 'T-Shirts', desc: 'A staple category featuring both basic essentials and graphic tees. Great for building a versatile everyday wardrobe without breaking the bank.' },
                    { name: 'Jackets', desc: 'Whether you need a lightweight windbreaker or a heavy winter coat, the jackets category has options for every climate and style preference.' },
                    { name: 'Pants / Shorts', desc: 'From tailored trousers to casual shorts, this category helps you complete your outfit from the bottom up with quality finds.' },
                    { name: 'Headwear', desc: 'Caps, beanies, bucket hats, and more — the headwear category adds the perfect finishing touch to any look.' },
                    { name: 'Sets', desc: 'Matching outfits and coordinated sets that take the guesswork out of putting together a cohesive look, all in one convenient entry.' },
                    { name: 'Underwear / Underpants', desc: 'Essential basics that prioritize comfort and quality, with options ranging from everyday staples to premium selections.' },
                    { name: 'Jersey', desc: 'Sports jerseys and athletic wear for fans and athletes alike. This category covers team gear and performance-oriented apparel.' },
                    { name: 'Accessories', desc: 'Watches, bracelets, sunglasses, and other add-ons that elevate any outfit. A treasure trove for those who love the details.' },
                    { name: 'Bags', desc: 'Backpacks, handbags, messenger bags, and travel luggage — practical and stylish options for carrying your essentials.' },
                    { name: 'Belts', desc: 'Leather belts, canvas belts, and statement pieces that tie your outfit together both literally and stylistically.' },
                    { name: 'Perfumes', desc: 'Fragrances for every preference, from fresh and floral to deep and musky. A carefully selected collection of scents.' },
                    { name: 'Other Stuff', desc: 'The catch-all category for unique finds that do not fit neatly into the other groups — often containing some of the most interesting discoveries.' },
                  ].map((cat) => (
                    <div key={cat.name} className="bg-background-50 rounded-xl p-4 border border-background-200/60">
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1">{cat.name}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{cat.desc}</p>
                    </div>
                  ))}
                </div>
                <p>
                  Having this many categories organized in one place means you can seamlessly browse across product types without switching platforms. The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">category grid on our homepage</Link> makes navigation intuitive and visually engaging.
                </p>
              </section>

              {/* Section 4 */}
              <section id="benefits" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  4. Key Benefits of Using a Product Spreadsheet
                </h2>
                <p className="mb-4">
                  Why use a spreadsheet instead of just searching manually? The benefits go far beyond simple convenience. Here is what makes the Kakobuy Spreadsheet approach so powerful:
                </p>
                <div className="space-y-4 mb-6">
                  {[
                    { title: 'Massive Time Savings', desc: 'The average shopper spends 45-60 minutes searching for a single product. With an organized database, that time shrinks to under 5 minutes. The spreadsheet eliminates the need to sift through irrelevant results, dead links, and low-quality listings.' },
                    { title: 'Curated Quality', desc: 'Every entry in the Kakobuy Spreadsheet has been reviewed for quality. This curation means you are not gambling on unknown sellers — you are browsing a pre-vetted collection of products that have passed quality checks.' },
                    { title: 'Cross-Platform Comparison', desc: 'The spreadsheet aggregates products from multiple sources, allowing you to compare prices, quality, and availability across platforms without opening a dozen browser tabs.' },
                    { title: 'Beginner Accessibility', desc: 'If you are new to organized product discovery, the spreadsheet removes the intimidation factor. Clear categories, helpful guides, and safety information make the experience approachable for everyone.' },
                  ].map((benefit) => (
                    <div key={benefit.title} className="flex items-start gap-4 bg-background-100 rounded-xl p-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-check-line text-base"></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{benefit.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5 */}
              <section id="comparison" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  5. Kakobuy vs Other Spreadsheet Platforms
                </h2>
                <p className="mb-4">
                  The Kakobuy Spreadsheet is not the only product database available. Other platforms like <strong>CSSBuy Spreadsheet</strong>, <strong>Superbuy Spreadsheet</strong>, and <strong>Oopbuy Spreadsheet</strong> also offer organized product discovery. Understanding the differences can help you choose the right resource for your needs.
                </p>
                <p className="mb-4">
                  <strong>CSSBuy Spreadsheet</strong> is particularly strong in the fashion and sneaker categories, with detailed QC photos and a dedicated community. <strong>Superbuy Spreadsheet</strong> offers a wide variety of general shopping categories and has an established user base with extensive reviews. <strong>Oopbuy Spreadsheet</strong> focuses on budget-friendly finds, making it a good starting point for first-time explorers.
                </p>
                <p>
                  However, the Kakobuy Spreadsheet stands out for its comprehensive category coverage, frequent updates, and beginner-friendly approach. The database includes dedicated buying guides, safety checklists, and community resources that make it more than just a list of links — it is a complete shopping companion. For most users, starting with the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Kakobuy Hub</Link> and then exploring other platforms as needed provides the best overall experience.
                </p>
              </section>

              {/* Section 6 */}
              <section id="safety" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  6. Safety Guide: How to Shop Securely
                </h2>
                <p className="mb-4">
                  While the Kakobuy Spreadsheet makes shopping easier, it is still important to follow basic safety practices. Here are essential guidelines every shopper should follow:
                </p>
                <div className="space-y-3 mb-6">
                  {[
                    { label: 'Verify Links', text: 'Always double-check that product links go where you expect them to. The spreadsheet is regularly updated, but verifying links before clicking adds an extra layer of security.' },
                    { label: 'Review QC Photos', text: 'Quality check photos are included with many entries. Take the time to inspect them carefully — they often reveal details that product descriptions miss.' },
                    { label: 'Calculate Total Costs', text: 'Factor in shipping, handling, and any additional fees before placing an order. Unexpected costs are one of the most common frustrations for online shoppers.' },
                    { label: 'Research Sellers', text: 'Look for seller ratings and community feedback when available. A few extra minutes of research can save you from a disappointing purchase.' },
                    { label: 'Start Small', text: 'If you are trying a new seller or category for the first time, consider placing a smaller order to test the waters before committing to larger purchases.' },
                  ].map((tip) => (
                    <div key={tip.label} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-shield-check-line text-xs"></i>
                      </div>
                      <div>
                        <strong className="text-sm text-foreground-900">{tip.label}:</strong>{' '}
                        <span className="text-sm text-foreground-600">{tip.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 7 */}
              <section id="tips" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  7. Advanced Tips for Power Shoppers
                </h2>
                <p className="mb-4">
                  Once you have mastered the basics, these advanced strategies will help you get even more value from the Kakobuy Spreadsheet:
                </p>
                <ul className="space-y-3 mb-6 text-sm text-foreground-700 list-disc list-inside">
                  <li><strong>Cross-reference categories:</strong> Some products appear in multiple categories. Checking related sections can surface hidden gems you might otherwise miss.</li>
                  <li><strong>Bookmark your favorites:</strong> Create a personal shortlist of go-to sellers and products so you can quickly return to them without searching again.</li>
                  <li><strong>Check update frequency:</strong> New products are added regularly. Checking the database weekly ensures you catch fresh finds before they sell out or links expire.</li>
                  <li><strong>Combine with community feedback:</strong> Many spreadsheet users share their experiences in discussion forums. Combining database information with real user reviews gives you the most complete picture.</li>
                  <li><strong>Use the comparison tools:</strong> If you are deciding between multiple similar products, the cross-platform comparison data in the spreadsheet can help you make a more informed choice.</li>
                </ul>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  8. Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    { q: 'Is the Kakobuy Spreadsheet free to use?', a: 'Yes, the spreadsheet database is freely accessible. You can browse categories, view product links, and access guides without any cost. The main Kakobuy Spreadsheet platform at kaspreadsheet.com provides the full database experience.' },
                    { q: 'How often is the spreadsheet updated?', a: 'The database receives frequent updates — typically on a weekly basis with new product entries, updated links, and refreshed category content. This ensures that the information stays current and relevant for shoppers.' },
                    { q: 'Do I need any special software to use the spreadsheet?', a: 'No, the Kakobuy Spreadsheet is designed to be accessible through a standard web browser. You can access it from any device — desktop, laptop, tablet, or mobile phone — without installing any additional software.' },
                    { q: 'Is the spreadsheet safe and reliable?', a: 'Yes, the database is maintained with quality and safety in mind. However, we always recommend following the safety checklist above: verify links, check QC photos, calculate total costs, and research sellers before making a purchase.' },
                    { q: 'Can I contribute products to the spreadsheet?', a: 'The spreadsheet is curated by the Kakobuy team to maintain quality standards. While direct user submissions may not be accepted, you can always reach out through the main website with suggestions or feedback.' },
                    { q: 'What makes Kakobuy Spreadsheet different from other product databases?', a: 'The Kakobuy Spreadsheet offers the most comprehensive category coverage (14 categories), frequent updates, beginner-friendly guides, and an integrated safety center. The combination of breadth, quality control, and educational resources sets it apart.' },
                  ].map((faq) => (
                    <div key={faq.q} className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">
                        <i className="ri-question-answer-line text-primary-500 mr-2 text-sm"></i>
                        {faq.q}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Conclusion */}
              <section className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Start Exploring the Kakobuy Spreadsheet Today
                </h2>
                <p className="mb-4">
                  The Kakobuy Spreadsheet represents a smarter way to shop — one that prioritizes organization, quality, and efficiency over endless manual searching. With 14 curated categories, thousands of product entries, and a beginner-friendly approach, it is the ideal resource for anyone looking to streamline their product discovery process.
                </p>
                <p className="mb-4">
                  Whether you are hunting for the latest sneakers, building a new wardrobe, or simply curious about what is available, the spreadsheet database puts organized information at your fingertips. The combination of curated finds, cross-platform comparison data, and safety resources creates a shopping experience that is both faster and more enjoyable than traditional methods.
                </p>
                <p>
                  Ready to get started? Head back to the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Kakobuy Hub homepage</Link> to browse categories, explore comparisons, and access the full spreadsheet database. Your next great find is just a few clicks away.
                </p>
              </section>

            </div>

            {/* Article Footer CTA */}
            <div className="mt-12 p-8 bg-background-100 rounded-2xl border border-background-200/60 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                Continue Exploring
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Browse the complete Kakobuy Spreadsheet database with 14 categories of curated products.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line ml-1"></i>
                </a>
              </div>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}